import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { checkAndFillInitialPages } from "../../utils/customization/content";
import PageLoader from "../../components/atoms/PageLoader";
import CustomizationStateProvider from "../../components/CustomizationStateProvider";
import Customization from "../../components/organisms/customization/Customization";
import {
  CustomizationOnQuit,
  CustomizationOnSave,
  CustomizationTrack,
} from "../../components/organisms/customization/CustomizationHeader";
import KustomApiProvider from "../../providers/customization/KustomApiProvider";
import { Configuration } from "../../types/customization/configuration";
import { Content } from "../../types/customization/content";
import { Template } from "../../types/customization/template";
import { convertFromContentFormat } from "../../utils/customization/format/content";
import { convertFromTemplateFormat } from "../../utils/customization/format/template";
import { isTouchDevice } from "../../utils/customization/device";

interface Props {
  contentId: string;
  configuration: Configuration;
  onQuit: CustomizationOnQuit;
  templateRevisionId: string;
  track?: CustomizationTrack;
}

const CustomizationContainer: FunctionComponent<Props> = ({
  contentId,
  configuration,
  onQuit,
  templateRevisionId,
  track,
}) => {
  const [assetUrlTemplate, setAssetUrlTemplate] = useState<string>();
  const [content, setContent] = useState<Content>();
  const [template, setTemplate] = useState<Template>();
  const [loading, setLoading] = useState<boolean>(true);
  const { kustomApiToken } = configuration;

  const kustomApiProvider = useMemo(() => {
    return new KustomApiProvider(kustomApiToken);
  }, [kustomApiToken]);

  useEffect(() => {
    setLoading(true);

    kustomApiProvider
      .getTemplateRevision(templateRevisionId)
      .then((templateResponse) => {
        const convertedTemplate = convertFromTemplateFormat(templateResponse.definition);
        setTemplate(convertedTemplate);
        setAssetUrlTemplate(templateResponse.asset_url_template);

        return kustomApiProvider.getContent(contentId).then((contentResponse) => {
          const convertedContent = convertFromContentFormat(
            contentResponse.definition,
            convertedTemplate
          );
          // add initial pages from configuration
          const filledPages = checkAndFillInitialPages(
            convertedContent,
            convertedTemplate,
            configuration.options
          );
          if (filledPages.length > convertedContent.pages.length) {
            setContent({ ...convertedContent, pages: filledPages });
          } else {
            setContent(convertedContent);
          }
        });
      })
      .catch(() => {
        return;
      });

    setLoading(false);
  }, [contentId, kustomApiProvider, templateRevisionId]);

  const onCustomizationSave: CustomizationOnSave = useCallback(
    (newContent: Content) => {
      return kustomApiProvider.saveContent(contentId, newContent).then((contentResponse) => {
        if (!template) {
          // todo : manage an error here
          return {
            definitionVersion: 8,
            locale: newContent.locale,
            pages: [],
            selectionPhotos: [],
            templateType: newContent.templateType,
          } as Content;
        }

        const contentSaved = convertFromContentFormat(contentResponse.definition, template);
        setContent(contentSaved);
        return contentSaved;
      });
    },
    [contentId, kustomApiProvider, template]
  );

  return (
    <>
      <PageLoader fullPage={true} loaded={!loading} />

      {!loading && template && content ? (
        <DndProvider backend={isTouchDevice() ? TouchBackend : HTML5Backend} debugMode={true}>
          <CustomizationStateProvider
            configuration={{
              ...configuration,
              assetUrlTemplate,
            }}
            initialState={{
              content,
              template,
              track,
            }}
          >
            <Customization onQuit={onQuit} onSave={onCustomizationSave} />
          </CustomizationStateProvider>
        </DndProvider>
      ) : null}
    </>
  );
};

export default CustomizationContainer;
